import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatCzDate } from "@/lib/datetime";
import { useRowMutation } from "@/lib/use-api-mutation";
import { useState } from "react";

/**
 * "Send to existing report" — pours a ticked selection (time entries, or
 * fixed-price work items) of a single customer into one of that
 * customer's open DRAFT reports.
 *
 * Only drafts are listed: a sent / paid report is already on a real
 * Fakturoid invoice, so growing it would silently desync the two. The
 * server enforces the same-customer + billing-state contract; this
 * dialog is just the picker. The accountant clicks a draft, the items
 * are appended, and the list refreshes via `onDone`.
 */

type DraftBatch = {
  id: string;
  periodFrom: string;
  periodTo: string;
  totalAmount: string;
  _count: { items: number };
};

type Props = {
  /** What is being sent — decides the endpoint and the wording. */
  kind: "entries" | "work-items";
  ids: string[];
  customerId: string;
  customerName: string | null;
  /** Called after the items are successfully appended — clears selection
   *  and reloads the listing. */
  onDone: () => void;
  /** Trigger element — the bulk bar's "Send to existing report" button. */
  children: React.ReactNode;
};

const KIND = {
  entries: { endpoint: "add-entries", body: "timeEntryIds", one: "entry", many: "entries" },
  "work-items": {
    endpoint: "add-work-items",
    body: "workItemIds",
    one: "work item",
    many: "work items",
  },
} as const;

export function AddToDraftReportDialog({
  kind,
  ids,
  customerId,
  customerName,
  onDone,
  children,
}: Props) {
  const { endpoint, body, one, many } = KIND[kind];
  const noun = ids.length === 1 ? one : many;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<DraftBatch[] | null>(null);

  async function loadDrafts() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        customerId,
        status: "draft",
        pageSize: "200",
      });
      const r = await api.get<{ batches: DraftBatch[] }>(`/api/billing?${params.toString()}`);
      setDrafts(r.batches);
    } catch (err) {
      toast({
        title: "Couldn't load reports",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }

  const send = useRowMutation(
    (batchId: string) =>
      api.post<{ added: number }>(`/api/billing/${batchId}/${endpoint}`, { [body]: ids }),
    {
      rowKey: (batchId) => batchId,
      onSuccess: ({ added }) => {
        toast({
          title: `${added} ${added === 1 ? one : many} added to the report`,
          variant: "success",
        });
        setOpen(false);
        onDone();
      },
      errorMessage: (err) => (err instanceof Error ? err.message : "Error"),
      onError: (msg) =>
        toast({ title: "Couldn't add to report", description: msg, variant: "destructive" }),
    },
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) void loadDrafts();
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send to existing report</DialogTitle>
          <DialogDescription>
            Add {ids.length} {noun} to an open draft report
            {customerName ? ` for ${customerName}` : ""}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading reports…</p>
        ) : drafts && drafts.length > 0 ? (
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {drafts.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  disabled={send.busy}
                  onClick={() => send.run(b.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-md border bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <span>
                    <span className="font-medium">
                      {formatCzDate(b.periodFrom)} – {formatCzDate(b.periodTo)}
                    </span>
                    <span className="text-muted-foreground">
                      {" · "}
                      {b._count.items} {b._count.items === 1 ? "line" : "lines"}
                    </span>
                  </span>
                  <span className="whitespace-nowrap text-muted-foreground">
                    {send.isRunning(b.id)
                      ? "Adding…"
                      : `${Number(b.totalAmount).toLocaleString()} Kč`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No open draft reports for this customer. Use “Create report” instead.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
